/**
 * 请假记录 API
 * 后端接口：
 * - /api/leave-records
 * - /api/leave-records/export
 * - /api/leave/apply
 * - /api/leave/approve
 */
import { request, requestRaw, buildUrl } from './request'

/** status 数字 -> 展示文案 */
export const LEAVE_STATUS_MAP = {
  0: '待审批',
  1: '已通过',
  2: '已驳回',
  3: '已销假'
}

/** 展示文案 -> status 数字 */
export const LEAVE_STATUS_TO_CODE = {
  待审批: 0,
  已通过: 1,
  已驳回: 2,
  已销假: 3
}

/** 解析请假状态 */
const APPROVAL_STATUS_TO_CODE = {
  pending: 0,
  approved: 1,
  rejected: 2,
  cancelled: 3,
  canceled: 3
}

function toNumber(value, fallback = 0) {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : fallback
}

export function resolveLeaveStatusCode(row = {}) {
  const candidates = [
    row?.approval_status,
    row?.approvalStatus,
    row?.instance_status,
    row?.instanceStatus,
    row?.status
  ]

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === '') continue

    if (typeof candidate === 'string') {
      const text = candidate.trim()
      if (LEAVE_STATUS_TO_CODE[text] !== undefined) return LEAVE_STATUS_TO_CODE[text]

      const normalized = text.toLowerCase()
      if (APPROVAL_STATUS_TO_CODE[normalized] !== undefined) {
        return APPROVAL_STATUS_TO_CODE[normalized]
      }
    }

    const numeric = Number(candidate)
    if (Number.isFinite(numeric)) return numeric
  }

  return 0
}

export function parseLeaveStatus(row = {}) {
  const statusCode = resolveLeaveStatusCode(row)
  return LEAVE_STATUS_MAP[statusCode] ?? LEAVE_STATUS_MAP[0]
}

export function extractLeaveRecordList(response) {
  const root = response?.data ?? response ?? {}
  if (Array.isArray(root?.records)) return root.records
  if (Array.isArray(root?.list)) return root.list
  if (Array.isArray(root)) return root
  if (Array.isArray(response?.records)) return response.records
  if (Array.isArray(response?.list)) return response.list
  return []
}

export function extractLeavePagination(response, fallbackPage = 1, fallbackPageSize = 20, listLength = 0) {
  const root = response?.data ?? response ?? {}
  const paginationCandidate = response?.pagination ?? root?.pagination
  const pagination = paginationCandidate && typeof paginationCandidate === 'object' ? paginationCandidate : {}
  const currentPage = toNumber(
    pagination.current_page ??
    pagination.currentPage ??
    pagination.page ??
    root?.current_page ??
    root?.currentPage ??
    root?.page ??
    response?.page,
    fallbackPage
  ) || fallbackPage
  const pageSize = toNumber(
    pagination.page_size ??
    pagination.pageSize ??
    root?.page_size ??
    root?.pageSize ??
    response?.page_size,
    fallbackPageSize
  ) || fallbackPageSize
  const total = toNumber(
    pagination.total_count ??
    pagination.totalCount ??
    pagination.total ??
    root?.total_count ??
    root?.totalCount ??
    root?.total ??
    response?.count ??
    response?.total,
    0
  ) || 0
  const totalPages = toNumber(
    pagination.total_pages ??
    pagination.totalPages ??
    root?.total_pages ??
    root?.totalPages,
    total ? Math.ceil(total / Math.max(pageSize, 1)) : 0
  ) || 0
  const hasMore = typeof pagination.has_next === 'boolean'
    ? pagination.has_next
    : typeof pagination.has_more === 'boolean'
      ? pagination.has_more
      : totalPages > 0
        ? currentPage < totalPages
        : listLength >= pageSize

  return {
    currentPage,
    pageSize,
    total,
    totalPages,
    hasMore
  }
}

export function normalizeLeaveListParams(params = {}) {
  const payload = { ...params }
  if (payload.leave_type == null && payload.type != null) {
    payload.leave_type = payload.type
  }
  delete payload.type

  if (payload.class_id == null && payload.classId != null) {
    payload.class_id = payload.classId
  }
  delete payload.classId

  if (payload.class_name == null && payload.className != null) {
    payload.class_name = payload.className
  }
  delete payload.className

  if (payload.grade_id == null && payload.gradeId != null) {
    payload.grade_id = payload.gradeId
  }
  delete payload.gradeId

  if (payload.grade_name == null && payload.gradeName != null) {
    payload.grade_name = payload.gradeName
  }
  delete payload.gradeName

  if (typeof payload.status === 'string' && LEAVE_STATUS_TO_CODE[payload.status] !== undefined) {
    payload.status = LEAVE_STATUS_TO_CODE[payload.status]
  }

  if (payload.page == null || Number(payload.page) < 1) payload.page = 1
  if (payload.page_size == null || Number(payload.page_size) < 1) payload.page_size = 20
  if (payload.page_size != null && Number(payload.page_size) > MAX_PAGE_SIZE) {
    payload.page_size = MAX_PAGE_SIZE
  }

  return payload
}

function buildQuery(params) {
  const search = new URLSearchParams()
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, value)
  })
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

function parseDownloadFileName(contentDisposition, fallback) {
  if (!contentDisposition) return fallback

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch {
      return utf8Match[1]
    }
  }

  const simpleMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
  return simpleMatch?.[1] || fallback
}

async function extractLeaveDownloadError(response, fallbackMessage) {
  const payload = await response.json().catch(() => null)
  if (payload?.message) return payload.message
  if (payload?.error) return payload.error
  if (payload?.msg) return payload.msg
  return fallbackMessage
}

/** 请假列表 page_size 最大值（后端限制） */
const MAX_PAGE_SIZE = 100

/**
 * 获取请假记录列表
 * @param {object} params - { page?, page_size?, status?, type?, student_name?, class_id? }
 * @returns {Promise<{ success, data, count, pagination }>}
 */
export function getLeaveRecords(params = {}) {
  return request(`/api/leave-records${buildQuery(normalizeLeaveListParams(params))}`)
}

export function getLeaveDashboard(params = {}) {
  return request(`/api/leave/dashboard${buildQuery({
    range_type: params.range_type,
    start_date: params.start_date,
    end_date: params.end_date,
    grade_id: params.grade_id,
    grade: params.grade,
    class_id: params.class_id
  })}`)
}

/**
 * 教师查看所管班级请假记录
 * GET /api/leave/class
 */
export function getLeaveRecordsByClass(params = {}) {
  const payload = { ...params }
  if (payload.leave_type == null && payload.type != null) {
    payload.leave_type = payload.type
  }
  if (payload.class_id == null && payload.classId != null) {
    payload.class_id = payload.classId
  }
  if (payload.class_name == null && payload.className != null) {
    payload.class_name = payload.className
  }
  if (typeof payload.status === 'string' && LEAVE_STATUS_TO_CODE[payload.status] !== undefined) {
    payload.status = LEAVE_STATUS_TO_CODE[payload.status]
  }

  return request(`/api/leave/class${buildQuery({
    class_id: payload.class_id,
    class_name: payload.class_name,
    status: payload.status,
    leave_type: payload.leave_type,
    student_name: payload.student_name,
    page: payload.page,
    page_size: payload.page_size,
    start_date: payload.start_date,
    end_date: payload.end_date
  })}`)
}

export function getTeacherPendingLeaves(params = {}) {
  const payload = normalizeLeaveListParams(params)
  return request(`/api/leave/pending${buildQuery({
    page: payload.page,
    page_size: payload.page_size,
    class_id: payload.class_id,
    class_name: payload.class_name,
    status: payload.status,
    leave_type: payload.leave_type,
    student_name: payload.student_name,
    start_date: payload.start_date,
    end_date: payload.end_date
  })}`)
}

/**
 * 校医查看自己提交的代请假记录
 * GET /api/leave/submitted
 * @param {object} params - { start_date?, end_date? }
 */
export function getSubmittedLeaveRecords(params = {}) {
  return request(`/api/leave/submitted${buildQuery({
    start_date: params.start_date,
    end_date: params.end_date
  })}`)
}

/**
 * 导出请假记录 Excel
 * GET /api/leave-records/export
 * @param {object} params
 * @param {string} fallbackFileName
 * @returns {Promise<{ blob: Blob, fileName: string }>}
 */
export async function exportLeaveRecords(params = {}, fallbackFileName = '请假记录导出.xlsx') {
  const url = buildUrl('/api/leave-records/export', {
    status: params.status,
    leave_type: params.leave_type,
    student_name: params.student_name,
    grade_id: params.grade_id ?? params.gradeId,
    class_id: params.class_id,
    grade: params.grade,
    class_name: params.class_name,
    start_date: params.start_date,
    end_date: params.end_date
  })

  const response = await requestRaw(url)

  if (!response.ok) {
    const message = await extractLeaveDownloadError(response, '导出请假记录失败')
    const error = new Error(message)
    error.status = response.status
    throw error
  }

  const blob = await response.blob()
  const fileName = parseDownloadFileName(
    response.headers.get('content-disposition'),
    fallbackFileName
  )

  return { blob, fileName }
}

/**
 * 根据 ID 获取请假记录详情
 * @param {number|string} id
 * @returns {Promise<{ success, data }>}
 */
export function getLeaveRecordDetail(id) {
  return request(`/api/leave-records/${id}`)
}

/**
 * 学生申请请假
 * POST /api/leave/apply
 * @param {object} data
 */
export function applyLeave(data) {
  return request('/api/leave/apply', {
    method: 'POST',
    body: data
  })
}

/**
 * 医务室紧急放行
 * POST /api/medical/leave/emergency-approve
 * @param {number|string} leaveId
 * @param {object} data - { approval_comment? }
 */
export function emergencyApproveMedicalLeave(leaveId, data = {}) {
  const normalizedLeaveId = Number(leaveId)
  return request('/api/medical/leave/emergency-approve', {
    method: 'POST',
    body: {
      leave_id: Number.isFinite(normalizedLeaveId) ? normalizedLeaveId : leaveId,
      approval_comment: data.approval_comment
    }
  })
}

/**
 * 审批请假
 * POST /api/leave/approve
 * @param {number|string} leaveId
 * @param {object} data - { approved?, status?, comment?, approval_comment? }
 * @returns {Promise<{ success, message, data?: { leave_id, status } }>}
 */
export function approveLeave(leaveId, data = {}) {
  const normalizedLeaveId = Number(leaveId)
  const approved = typeof data.approved === 'boolean'
    ? data.approved
    : Number(data.status) === 1
      ? true
      : Number(data.status) === 2
        ? false
        : undefined
  const status = Number(data.status) || (approved === true ? 1 : approved === false ? 2 : undefined)
  const comment = data.comment ?? data.approval_comment ?? data.approvalComment ?? ''

  return request('/api/leave/approve', {
    method: 'POST',
    body: {
      leave_id: Number.isFinite(normalizedLeaveId) ? normalizedLeaveId : leaveId,
      approved,
      status,
      comment,
      approval_comment: comment
    }
  })
}

/**
 * 销假
 * POST /api/leave/finish
 * @param {number|string} leaveId
 * @returns {Promise<{ success, message, data?: { leave_id, status } }>}
 */
export function finishLeaveRecord(leaveId) {
  const normalizedLeaveId = Number(leaveId)

  return request('/api/leave/finish', {
    method: 'POST',
    body: {
      leave_id: Number.isFinite(normalizedLeaveId) ? normalizedLeaveId : leaveId
    }
  })
}

/**
 * 创建请假记录
 * @param {object} data
 */
export function createLeaveRecord(data) {
  return request('/api/leave-records', {
    method: 'POST',
    body: JSON.stringify(data)
  })
}

/**
 * 更新请假记录
 * @param {number|string} id
 * @param {object} data
 */
export function updateLeaveRecord(id, data) {
  return request(`/api/leave-records/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

/**
 * 删除请假记录
 * @param {number|string} id
 */
export function deleteLeaveRecord(id) {
  return request(`/api/leave-records/${id}`, {
    method: 'DELETE'
  })
}
